import React, { useEffect, useState } from "react";
import { Checkbox, Col, Form, message, Modal, Row, Skeleton } from "antd";

import { useFetchConstant } from "apis/constant";

import FormInput from "components/FormInput/FormInput";
import FormPolicySuggestions from "components/FormPolicySuggestions/FormPolicySuggestions";
import FormSelection from "components/FormSelection/FormSelection";
import SectionCard from "components/SectionCard/SectionCard";
import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";
import FormDynamicTable from "components/FormDynamicTable/FormDynamicTable";
import EditableTable from "components/EditableTable/EditableTable";
import { constructDisplayPrice } from "utils/general";
import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import moment from "moment";
import Locale from "react-intl-universal";

const { useForm, Item: FormItem } = Form;

const AddPolicyModal = ({
  visible,
  onOk,
  onCancel,
  defaultValues,
  isForProposal,
}) => {
  const [form] = useForm();
  const {
    isLoading: isPlanTypesLoading,
    selection: planTypes,
  } = useFetchConstant("planTypes");
  const {
    isLoading: isInsuranceCompaniesLoading,
    selection: insuranceCompanies,
    data: insuranceCompaniesConst,
  } = useFetchConstant("insuranceCompanies");
  const {
    isLoading: isPaymentModesLoading,
    selection: paymentModes,
  } = useFetchConstant("paymentModes");
  const isConstantsLoading =
    isPlanTypesLoading || isInsuranceCompaniesLoading || isPaymentModesLoading;

  const [selectedSuggestedPolicy, setSelectedSuggestedPolicy] = useState({});
  const [selectedCompany, setSelectedCompany] = useState("");
  const [
    shouldShowOtherCompanyInput,
    setShouldShowOtherCompanyInput,
  ] = useState(false);
  const [formattedDefaultValues, setFormattedDefaultValues] = useState({});
  const [selectedType, setSelectedType] = useState("");
  const [hasMedicalCard, setHasMedicalCard] = useState(false);
  const [
    isLifetimeLimitAmtUnlimited,
    setIsLifetimeLimitAmtUnlimited,
  ] = useState(false);
  const [lifetimeLimitAmt, setLifetimeLimitAmt] = useState("");
  const [extraCoverage, setExtraCoverage] = useState([]);
  const isNew = !defaultValues || !Object.keys(defaultValues).length > 0;

  const formatExportingValues = (values) => {
    const formatBenefits = ({
      waivers,
      benefits,
      hospitalizations,
      femaleBenefits,
    }) => {
      const getBenefitsArray = (benefits) =>
        Object.entries(benefits).reduce((benefitsArr, [key, amount]) => {
          if (amount) {
            return [...benefitsArr, { type: key, amount: Number(amount) }];
          }
          return benefitsArr;
        }, []);

      const keysOfWaivers =
        waivers && Object.keys(waivers).filter((key) => waivers[key]);

      const benefitsArr = [
        ...getBenefitsArray(benefits),
        ...values.extraCoverage,
      ];
      const hospitalizationsArr = isForProposal
        ? getBenefitsArray(hospitalizations)
        : [];
      const waiversArr = keysOfWaivers.map((key) => ({ type: key }));

      const femaleBenefitsArr = getBenefitsArray(femaleBenefits);
      return {
        planCoverages: [...benefitsArr, ...waiversArr],
        hospitalizationBenefits: hospitalizationsArr,
        femaleBenefits: femaleBenefitsArr,
      };
    };

    return {
      ...values,
      commencementDate: values.commencementDate
        ? moment(values.commencementDate).format("YYYY-MM-DD")
        : undefined,
      maturityDate: values.maturityDate
        ? moment(values.maturityDate).format("YYYY-MM-DD")
        : undefined,
      benefits: formatBenefits(values),
      premium: values.premium && Number(values.premium),
      medicalCard: values.medicalCard && {
        ...values.medicalCard,
        yearlyLimitAmt:
          values.medicalCard.yearlyLimitAmt &&
          values.medicalCard.yearlyLimitAmt &&
          Number(values.medicalCard.yearlyLimitAmt),
        lifetimeLimitAmt: values.medicalCard.isLifetimeLimitAmtUnlimited
          ? -1
          : values.medicalCard.lifetimeLimitAmt &&
            Number(values.medicalCard.lifetimeLimitAmt),
      },
    };
  };

  const formatBenefits = (coverages = []) => {
    const benefitInObj = {};
    coverages.forEach(
      (coverage) => (benefitInObj[coverage.type] = coverage.amount)
    );
    return Object.keys(benefitInObj).length > 0 ? benefitInObj : undefined;
  };

  const formatWaivers = (coverages = []) => {
    const waiversInObj = {};
    coverages
      .filter(
        (coverage) =>
          coverage.type === "tpdWaiver" || coverage.type === "ciWaiver"
      )
      .forEach((coverage) => (waiversInObj[coverage.type] = true));
    return Object.keys(waiversInObj).length > 0 ? waiversInObj : undefined;
  };

  useEffect(() => {
    const policy = isNew
      ? Object.keys(selectedSuggestedPolicy).length > 0
        ? selectedSuggestedPolicy
        : {}
      : defaultValues;
    const isLifetimeLimitAmtUnlimitedFormValue =
      policy.medicalCard && policy.medicalCard.lifetimeLimitAmt < 0;
    const lifetimeLimitAmtFormValue =
      policy.medicalCard && policy.medicalCard.lifetimeLimitAmt > -1
        ? policy.medicalCard.lifetimeLimitAmt
        : undefined;
    setSelectedType(policy.type);
    if (policy.company) {
      setSelectedCompany(policy.company.type);
    }
    setIsLifetimeLimitAmtUnlimited(isLifetimeLimitAmtUnlimitedFormValue);
    setLifetimeLimitAmt(lifetimeLimitAmtFormValue);

    const formattedDefaultValues = {
      ...policy,
      commencementDate: policy.commencementDate
        ? moment(policy.commencementDate, "YYYY-MM-DD")
        : undefined,
      maturityDate: policy.maturityDate
        ? moment(policy.maturityDate, "YYYY-MM-DD")
        : undefined,
      hasMedicalCard: {
        value: policy.medicalCard && Object.keys(policy.medicalCard).length > 0,
      },
      medicalCard: policy.medicalCard && {
        ...policy.medicalCard,
        lifetimeLimitAmt: isLifetimeLimitAmtUnlimitedFormValue
          ? undefined
          : lifetimeLimitAmtFormValue,
        isLifetimeLimitAmtUnlimited: isLifetimeLimitAmtUnlimitedFormValue,
      },
      benefits:
        policy.benefits &&
        policy.benefits.planCoverages &&
        formatBenefits(policy.benefits.planCoverages),
      waivers:
        policy.benefits &&
        policy.benefits.planCoverages &&
        formatWaivers(policy.benefits.planCoverages),
      hospitalizations:
        policy.benefits &&
        policy.benefits.hospitalizationBenefits &&
        formatBenefits(policy.benefits.hospitalizationBenefits),
      femaleBenefits:
        policy.benefits &&
        policy.benefits.femaleBenefits &&
        formatBenefits(policy.benefits.femaleBenefits),
    };
    setFormattedDefaultValues(formattedDefaultValues);
    form.setFieldsValue({
      ...formattedDefaultValues,
    });

    if (
      defaultValues?.benefits?.planCoverages &&
      defaultValues.benefits.planCoverages.length > 0
    ) {
      setExtraCoverage(
        defaultValues.benefits.planCoverages.filter(
          (x) => x.type === "otherBenefit"
        )
      );
      console.log(
        defaultValues.benefits.planCoverages.filter(
          (x) => x.type === "otherBenefit"
        ),
        184
      );
    }
  }, [defaultValues, selectedSuggestedPolicy, isNew, form]);

  useEffect(() => {
    if (
      !isInsuranceCompaniesLoading &&
      insuranceCompaniesConst.OTHERS.code === selectedCompany
    ) {
      setShouldShowOtherCompanyInput(true);
    } else {
      setShouldShowOtherCompanyInput(false);
    }
  }, [selectedCompany, insuranceCompaniesConst, isInsuranceCompaniesLoading]);

  const handleOnOkButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const medicalCardValues = values.medicalCard && {
        ...values.medicalCard,
        lifetimeLimitAmt: values.medicalCard.isLifetimeLimitAmtUnlimited
          ? -1
          : values.medicalCard.lifetimeLimitAmt,
      };

      var newValues = formatExportingValues({
        ...values,
        medicalCard: medicalCardValues,
        totalCoverage: Object.values({
          ...values.benefits,
          ...values.hospitalizations,
        })
          .filter((benefit) => benefit)
          .reduce(
            (totalCoverage, filteredBenefit) =>
              totalCoverage + Number(filteredBenefit),
            0
          ),
      });

      newValues.status = defaultValues.status;
      newValues.policyNumber =
        values.policyNumber || defaultValues.policyNumber;
      onOk(newValues);
    } catch (error) {
      error &&
        error.errorFields &&
        error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const getModalTitle = () => {
    const coveredFor = defaultValues && defaultValues.coveredFor;
    return !!coveredFor
      ? `Policy Details - Covered for ${coveredFor}`
      : Locale.get("addNewAdvisee.policySection.modalTitle");
  };

  return (
    <Modal
      title={getModalTitle()}
      visible={visible}
      okText={Locale.get("addNewAdvisee.policySection.saveButton")}
      onOk={handleOnOkButtonClick}
      onCancel={onCancel}
      width="60%"
    >
      <Form form={form} initialValues={formattedDefaultValues}>
        {isConstantsLoading ? (
          <Skeleton active />
        ) : (
          <>
            <Row gutter={16}>
              <Col span={shouldShowOtherCompanyInput ? 24 : 12}>
                {!isNew ? (
                  <FormInput
                    label={Locale.get("addNewAdvisee.policySection.policyName")}
                    name="name"
                    placeholder={Locale.get(
                      "addNewAdvisee.policySection.policyNamePlaceholder"
                    )}
                    requiredErrorMessage={Locale.get(
                      "addNewAdvisee.policySection.errMsgPolicyName"
                    )}
                  />
                ) : (
                  <FormPolicySuggestions
                    onSelectPolicy={(selectedPolicy) =>
                      setSelectedSuggestedPolicy(selectedPolicy)
                    }
                  />
                )}
              </Col>
              {!isForProposal && (
                <Col span={12}>
                  <FormInput
                    label={Locale.get("addNewAdvisee.policySection.policyNo")}
                    name="policyNumber"
                    placeholder={Locale.get(
                      "addNewAdvisee.policySection.policyNoPlaceholder"
                    )}
                    requiredErrorMessage={Locale.get(
                      "addNewAdvisee.policySection.errMsgPolicyNo"
                    )}
                  />
                </Col>
              )}
              <Col span={12}>
                <FormSelection
                  label={Locale.get(
                    "addNewAdvisee.policySection.insuranceCompany"
                  )}
                  name={["company", "type"]}
                  placeholder={Locale.get(
                    "addNewAdvisee.policySection.insurancePlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "addNewAdvisee.policySection.errMsgInsurance"
                  )}
                  selections={insuranceCompanies}
                  onChange={setSelectedCompany}
                />
              </Col>
              {shouldShowOtherCompanyInput && (
                <Col span={12}>
                  <FormInput
                    label={<div style={{ height: 25 }}> </div>}
                    name={["company", "label"]}
                    placeholder={Locale.get(
                      "addNewAdvisee.policySection.otherPlaceholder"
                    )}
                    requiredErrorMessage={Locale.get(
                      "addNewAdvisee.policySection.errMsgOther"
                    )}
                  />
                </Col>
              )}
              <Col span={12}>
                <FormInput
                  label={Locale.get("addNewAdvisee.policySection.premium")}
                  name="premium"
                  placeholder={Locale.get(
                    "addNewAdvisee.policySection.premiumPlaceholder"
                  )}
                  type="financial"
                  requiredErrorMessage={Locale.get(
                    "addNewAdvisee.policySection.errMsgPremium"
                  )}
                />
              </Col>
              <Col span={12}>
                <FormSelection
                  label={Locale.get("addNewAdvisee.policySection.mop")}
                  name="paymentMode"
                  placeholder={Locale.get(
                    "addNewAdvisee.policySection.mopPlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "addNewAdvisee.policySection.errMsgMop"
                  )}
                  selections={paymentModes}
                />
              </Col>
              <Col span={12}>
                <FormSelection
                  label={Locale.get("addNewAdvisee.policySection.top")}
                  name="type"
                  placeholder={Locale.get(
                    "addNewAdvisee.policySection.topPlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "addNewAdvisee.policySection.errMsgTop"
                  )}
                  selections={planTypes}
                  extraProps={{
                    onChange: (value) => setSelectedType(value),
                  }}
                />
              </Col>
              <Col span={12}>
                <FormDatePicker
                  label={Locale.get(
                    "addNewAdvisee.policySection.commencementDate"
                  )}
                  name="commencementDate"
                  placeholder="2020-01-01"
                />
              </Col>
              <Col span={12}>
                <FormDatePicker
                  label={Locale.get("addNewAdvisee.policySection.maturityDate")}
                  name="maturityDate"
                  placeholder="2040-01-01"
                />
              </Col>
              {selectedType === "otherPlanType" && (
                <Col span={12}>
                  <FormInput
                    label={<div style={{ height: 25 }}> </div>}
                    name="otherTypeLabel"
                    placeholder={Locale.get(
                      "addNewAdvisee.policySection.otherPlanTypePlaceholder"
                    )}
                    requiredErrorMessage={Locale.get(
                      "addNewAdvisee.policySection.errMsgOtherPlanType"
                    )}
                  />
                </Col>
              )}
              {isForProposal && (
                <Col span={24}>
                  <FormInput
                    label={Locale.get(
                      "addNewAdvisee.policySection.policyRemark"
                    )}
                    name="remarks"
                    type="textArea"
                  />
                </Col>
              )}
            </Row>
            <SectionContainer>
              <SectionCard
                title={Locale.get(
                  "addNewAdvisee.policySection.sectionContainerTitle_0"
                )}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.deathBenefit"
                      )}
                      name={["benefits", "deathBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.cib")}
                      name={["benefits", "ciBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.tpdb")}
                      name={["benefits", "tpdBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.totalCib")}
                      name={["benefits", "totalEarlyCIBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.pab")}
                      name={["benefits", "personalAccidentBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.oadb")}
                      name={["benefits", "oldAgeDisabilityBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.pdmi")}
                      name={["benefits", "permanentDisabilityMonthlyIncome"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24}>
                    <Title type="toggle" marginBottom="16px">
                      {Locale.get("addNewAdvisee.policySection.waivePremium")}
                    </Title>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      name={["waivers", "tpdWaiver"]}
                      valuePropName="checked"
                      style={{ marginBottom: "8px !important" }}
                    >
                      <Checkbox>
                        {Locale.get("addNewAdvisee.policySection.checkBox_a")}
                      </Checkbox>
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      name={["waivers", "ciWaiver"]}
                      valuePropName="checked"
                      style={{ marginBottom: "8px !important" }}
                    >
                      <Checkbox>
                        {Locale.get("addNewAdvisee.policySection.checkBox_b")}
                      </Checkbox>
                    </FormItem>
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>

            <SectionContainer>
              <SectionCard
                title={Locale.get(
                  "addNewAdvisee.policySection.sectionCardTitle_1"
                )}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="extraCoverage">
                      <EditableTable
                        loading={isPlanTypesLoading}
                        initialValue={extraCoverage}
                        defaultValue={{
                          type: "otherBenefit",
                          amount: 0,
                          remarks: "",
                          label: "",
                        }}
                        columns={[
                          {
                            title: Locale.get(
                              "addNewAdvisee.policySection.columnTitle_a"
                            ),
                            dataIndex: "label",
                            key: "label",
                            editable: true,
                            editableType: "text",
                            width: "40%",
                            mandatory: true,
                            render: (value) => (
                              <div
                                style={{
                                  paddingLeft: 11,
                                  paddingRight: 11,
                                  paddingTop: 6,
                                  paddingBottom: 4,
                                  borderRadius: 4,
                                  border: "1px solid #e0e0e0",
                                  minHeight: 38,
                                }}
                              >
                                {value}
                              </div>
                            ),
                          },
                          {
                            // title: "Amount (RM)",
                            title: Locale.get(
                              "addNewAdvisee.policySection.columnTitle_b"
                            ),
                            dataIndex: "amount",
                            key: "amount",
                            editableType: "number",
                            editable: true,
                            mandatory: true,
                            render: (value) => (
                              <div
                                style={{
                                  paddingLeft: 11,
                                  paddingRight: 11,
                                  paddingTop: 6,
                                  paddingBottom: 4,
                                  borderRadius: 4,
                                  border: "1px solid #e0e0e0",
                                  minHeight: 38,
                                }}
                              >
                                {constructDisplayPrice(value, "")}
                              </div>
                            ),
                          },
                          {
                            title: Locale.get(
                              "addNewAdvisee.policySection.columnTitle_c"
                            ),
                            dataIndex: "remarks",
                            key: "remarks",
                            width: "30%",
                            editableType: "text",
                            editable: true,
                            render: (value) => (
                              <div
                                style={{
                                  paddingLeft: 11,
                                  paddingRight: 11,
                                  paddingTop: 6,
                                  paddingBottom: 4,
                                  borderRadius: 4,
                                  border: "1px solid #e0e0e0",
                                  minHeight: 38,
                                }}
                              >
                                {value}
                              </div>
                            ),
                          },
                        ]}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>

            <SectionContainer>
              <SectionCard
                title={Locale.get(
                  "addNewAdvisee.policySection.sectionCardTitle_2"
                )}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.cardName")}
                      name={["medicalCard", "name"]}
                      placeholder={Locale.get(
                        "addNewAdvisee.policySection.cardNamePlaceholder"
                      )}
                      extraProps={{
                        onChange: () => setHasMedicalCard(true),
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.rab")}
                      name={["medicalCard", "roomAndBoardAmt"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.yearlyLimit"
                      )}
                      name={["medicalCard", "yearlyLimitAmt"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      disabled={!!isLifetimeLimitAmtUnlimited}
                      label={Locale.get(
                        "addNewAdvisee.policySection.lifetimeLimit"
                      )}
                      name={["medicalCard", "lifetimeLimitAmt"]}
                      placeholder="0.00"
                      type="financial"
                      requiredErrorMessage={
                        !isLifetimeLimitAmtUnlimited &&
                        hasMedicalCard &&
                        Locale.get(
                          "addNewAdvisee.policySection.errMsgLifetimeLimit"
                        )
                      }
                      extraProps={{
                        onChange: (e) => setLifetimeLimitAmt(e.target.value),
                      }}
                    />
                    <FormItem
                      name={["medicalCard", "isLifetimeLimitAmtUnlimited"]}
                      valuePropName="checked"
                      style={{ marginTop: "-16px" }}
                    >
                      <Checkbox
                        disabled={!!lifetimeLimitAmt}
                        onClick={(e) =>
                          setIsLifetimeLimitAmtUnlimited(e.target.checked)
                        }
                      >
                        {Locale.get(
                          "addNewAdvisee.policySection.unlimitedCheckbox"
                        )}
                      </Checkbox>
                    </FormItem>
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>

            {isForProposal && (
              <SectionContainer>
                <SectionCard
                  title={Locale.get(
                    "addNewAdvisee.policySection.sectionCardTitle_4"
                  )}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormInput
                        label={Locale.get(
                          "addNewAdvisee.policySection.hospitalSurgical"
                        )}
                        name={[
                          "hospitalizations",
                          "hospitalAndSurgicalBenefit",
                        ]}
                        placeholder="0.00"
                        type="financial"
                      />
                    </Col>
                    <Col span={12}>
                      <FormInput
                        label={Locale.get(
                          "addNewAdvisee.policySection.postHospitalization"
                        )}
                        name={["hospitalizations", "postHospitalization"]}
                        placeholder="0.00"
                        type="financial"
                      />
                    </Col>
                    <Col span={12}>
                      <FormInput
                        label={Locale.get(
                          "addNewAdvisee.policySection.dailyBenefit"
                        )}
                        name={["hospitalizations", "dailyBenefit"]}
                        placeholder="0.00"
                        type="financial"
                      />
                    </Col>
                  </Row>
                </SectionCard>
              </SectionContainer>
            )}

            <SectionContainer>
              <SectionCard
                title={Locale.get(
                  "addNewAdvisee.policySection.sectionCardTitle_3"
                )}
              >
                <Row gutter={16}>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.cis")}
                      name={["femaleBenefits", "carsinomaInSituBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.fCancerBenefit"
                      )}
                      name={["femaleBenefits", "cancerBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.fdb")}
                      name={["femaleBenefits", "facialDisfigurementBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.medCheckupFee"
                      )}
                      name={["femaleBenefits", "medicalCheckupBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.retirementFund"
                      )}
                      name={["femaleBenefits", "retirementFundBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.pcb")}
                      name={["femaleBenefits", "pregnancyComplicationBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get("addNewAdvisee.policySection.dbca")}
                      name={[
                        "femaleBenefits",
                        "congenitalAnomaliesDeliveryBenefit",
                      ]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.dHospitalizationBenefit"
                      )}
                      name={["femaleBenefits", "dailyHospitalizationBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                  <Col span={24} lg={12}>
                    <FormInput
                      label={Locale.get(
                        "addNewAdvisee.policySection.dDueToChildBirth"
                      )}
                      name={["femaleBenefits", "childbirthDeathBenefit"]}
                      placeholder="0.00"
                      type="financial"
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddPolicyModal;
